const STORAGE_KEY = "prediction-market-portal.backend";

export function loadBackendConfig() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {
        url: "https://api.wen.nocturne-standards.org",
        apiKey: "",
        metadataBasicUser: "",
        metadataBasicPass: "",
      };
    }
    return {
      url: "https://api.wen.nocturne-standards.org",
      apiKey: "",
      metadataBasicUser: "",
      metadataBasicPass: "",
      ...JSON.parse(raw),
    };
  } catch {
    return {
      url: "https://api.wen.nocturne-standards.org",
      apiKey: "",
      metadataBasicUser: "",
      metadataBasicPass: "",
    };
  }
}

export function saveBackendConfig(config) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
}

/**
 * Operator writes (metadata / hide) go through same-origin Pages Functions on
 * the hosted site so the browser never sees api.wen Basic Auth. Locally we
 * still hit the gateway URL directly - set metadataBasicUser/Pass (?debug=1)
 * to the §5.4 htpasswd, or create on the hosted site.
 */
function useWriteProxy() {
  if (typeof location === "undefined") return false;
  const host = location.hostname;
  return (
    host === "wen.nocturne-standards.org" ||
    host === "www.wen.nocturne-standards.org" ||
    host.endsWith(".wen-portal.pages.dev") ||
    host.endsWith(".prediction-market-portal.pages.dev")
  );
}

function operatorWriteUrl(config, gatewayPath) {
  if (useWriteProxy()) {
    return `${location.origin}/api${gatewayPath}`;
  }
  return `${config.url}${gatewayPath}`;
}

function operatorWriteHeaders(config, proxy) {
  const headers = { "Content-Type": "application/json" };
  if (proxy) return headers;
  if (config.metadataBasicUser && config.metadataBasicPass) {
    headers.Authorization = `Basic ${btoa(`${config.metadataBasicUser}:${config.metadataBasicPass}`)}`;
  } else if (config.apiKey) {
    headers.Authorization = `Bearer ${config.apiKey}`;
  }
  return headers;
}

/** Turn nginx HTML / opaque 401 bodies into an operator-facing sentence. */
export function formatOperatorWriteError(status, bodyText) {
  const text = (bodyText || "").trim();
  const looksNginx =
    status === 401 &&
    (/Authorization Required/i.test(text) || /<html[\s>]/i.test(text));
  if (looksNginx) {
    if (useWriteProxy()) {
      return (
        `operator Basic Auth rejected by api.wen (HTTP ${status}). ` +
        `Set Pages secrets METADATA_BASIC_USER/PASS to the VPS htpasswd-metadata values and redeploy Functions ` +
        `(docs/prediction-market/prediction-market-deploy-runbook.md §6.2a).`
      );
    }
    return (
      `operator Basic Auth required on api.wen (HTTP ${status}). ` +
      `Create on https://wen.nocturne-standards.org (same-origin proxy), or open Create with ?debug=1 and set metadata Basic user/pass, ` +
      `or backfill with scripts/pm-backfill-metadata.sh.`
    );
  }
  if (text) return text.length > 280 ? `${text.slice(0, 280)}…` : text;
  return `HTTP ${status}`;
}

async function readOperatorWriteError(res) {
  const body = await res.text();
  return formatOperatorWriteError(res.status, body);
}

/** `includeHidden` surfaces operator-hidden markets - owner-only Browse view. */
export async function listMarkets(config, { includeHidden = false } = {}) {
  const qs = includeHidden ? "?include_hidden=true" : "";
  const res = await fetch(`${config.url}/markets${qs}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getMarket(config, id) {
  const res = await fetch(`${config.url}/markets/${id}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function listMarketTrades(config, id, limit = 100) {
  const res = await fetch(`${config.url}/markets/${id}/trades?limit=${limit}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function listMarketHistory(config, id) {
  const res = await fetch(`${config.url}/markets/${id}/history`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function listMarketsByCommitment(config, commitmentHex) {
  const res = await fetch(
    `${config.url}/markets/by-commitment/${encodeURIComponent(commitmentHex)}`
  );
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

/**
 * Local/dev: optional Basic (htpasswd) or Bearer when talking to api.wen /
 * a keyed gateway directly. Hosted: same-origin `/api/...` proxy.
 */
export async function postMarketMetadata(config, body) {
  const proxy = useWriteProxy();
  const res = await fetch(operatorWriteUrl(config, "/markets/metadata"), {
    method: "POST",
    ...(proxy ? {} : { credentials: "include" }),
    headers: operatorWriteHeaders(config, proxy),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readOperatorWriteError(res));
  return res.json();
}

/**
 * Operator-only hide flag. Same proxy rules as `postMarketMetadata`.
 */
export async function setMarketHidden(config, marketId, hidden) {
  const proxy = useWriteProxy();
  const res = await fetch(operatorWriteUrl(config, `/markets/${marketId}/hidden`), {
    method: "POST",
    ...(proxy ? {} : { credentials: "include" }),
    headers: operatorWriteHeaders(config, proxy),
    body: JSON.stringify({ hidden }),
  });
  if (!res.ok) throw new Error(await readOperatorWriteError(res));
  return res.json();
}

export async function createParticipant(config) {
  const res = await fetch(`${config.url}/pm/participants`, { method: "POST" });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getParticipant(config, participantId) {
  const res = await fetch(
    `${config.url}/pm/participants/${encodeURIComponent(participantId)}`
  );
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getParticipantByAccount(config, moonlightAccount) {
  const res = await fetch(
    `${config.url}/pm/participants/by-account/${encodeURIComponent(moonlightAccount)}`
  );
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function verifyParticipant(config, body) {
  const res = await fetch(`${config.url}/pm/participants/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function linkParticipantCommitment(config, participantId, tradingCommitmentHex) {
  const res = await fetch(
    `${config.url}/pm/participants/${encodeURIComponent(participantId)}/commitment`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ trading_commitment_hex: tradingCommitmentHex }),
    }
  );
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getParticipantActivity(config, participantId) {
  const res = await fetch(
    `${config.url}/pm/participants/${encodeURIComponent(participantId)}/activity`
  );
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

/**
 * Beta feedback - unauthenticated, rate-limited on the gateway.
 * `body`: `{ message, category?, context? }`
 */
export async function postFeedback(config, body) {
  const res = await fetch(`${config.url}/pm/feedback`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function listLeaderboard(config, limit = 50) {
  const res = await fetch(`${config.url}/pm/leaderboard?limit=${limit}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

/** Soft-onboard verified achievement (lab/test; not create-gated). */
export const PM_ONBOARD_VERIFIED = 8;

export async function getOnboardRoot(config) {
  const res = await fetch(`${config.url}/pm/achievements/onboard-root`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

/** @param {"onboard" | "streak"} band */
export async function getPublishStatus(config, band) {
  const b = band === "streak" ? "streak" : "onboard";
  const res = await fetch(`${config.url}/pm/achievements/publish-status?band=${b}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

/**
 * @typedef {{ achievement_type: number, token_id: number, via_claim: boolean, minted_at: string }} HeldType
 */

/**
 * Achievement stats for a trading-key commitment.
 * @typedef {{ commitment: string, streak: number, total_correct: number, total_markets: number, buy_volume_lux: number, qualifies_streak_3: boolean, held_types: HeldType[] }} AchievementStats
 */

/**
 * @param {object} config
 * @param {string} commitmentHex
 * @returns {Promise<AchievementStats>}
 */
export async function getAchievementStats(config, commitmentHex) {
  const hex = commitmentHex.trim().replace(/^0x/i, "");
  const res = await fetch(
    `${config.url}/pm/achievements/${encodeURIComponent(hex)}`
  );
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

/**
 * @typedef {{ state: string, expires_at: string | null }} AgentSessionSnapshot
 * @typedef {{ open_markets: number, collateral_lux: string }} PositionsSummary
 * @typedef {{ commitment_hex: string, trader_class: string, deployed_lux: string, fees_paid_lux: string, spend_cap_lux: string | null, session: AgentSessionSnapshot, positions_summary: PositionsSummary }} AgentSnapshot
 */

/**
 * Read-only WEN agent facility snapshot.
 * @param {object} config
 * @param {string} commitmentHex
 * @returns {Promise<AgentSnapshot>}
 */
export async function getAgentSnapshot(config, commitmentHex) {
  const hex = commitmentHex.trim().replace(/^0x/i, "");
  const res = await fetch(
    `${config.url}/pm/agents/${encodeURIComponent(hex)}`
  );
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getClaimProof(config, commitmentHex, { epoch, type = 1 } = {}) {
  if (epoch == null) throw new Error("epoch required for claim proof");
  const hex = commitmentHex.trim().replace(/^0x/i, "");
  const qs = new URLSearchParams({ epoch: String(epoch), type: String(type) });
  const res = await fetch(
    `${config.url}/pm/achievements/${encodeURIComponent(hex)}/claim-proof?${qs}`
  );
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}
